import type { ReadonlyMat3 } from 'gl-matrix';
import type { ReactElement } from 'react';
import type { DoseId, RoiId } from '~/domain/changes/ids';
import type { BlendSettings } from '~/domain/viewport/fusionBlend';
import type { DoseUnit } from '~/domain/dicom/dicomDose';
import { WindowLevelReadout } from '~/domain/dicom/intensityUnits';
import type { SctDensity } from '~/domain/image/sctDensityCurve';
import type { PointMm } from '~/domain/geometry/patientGeometry';
import type { IntensityHistogram } from '~/domain/patient/image3d';
import type { WindowLevel } from '~/domain/patient/windowLevel';
import type { ImageBinding } from '~/domain/patient/imageBinding';
import type {
    PetSeriesMetadata,
    SuvQuantification,
} from '~/domain/patient/petSuv';
import type { DensityOverrideAtPoint } from '~/domain/structures/densityOverrideAtPoint';
import type { PublishedFrame } from '~/domain/viewport/publishedFrame';
import {
    WindowingPreset,
    WindowLevelBounds,
} from '~/domain/patient/windowLevel';
import { DirectionLabel } from '~/utils/orientation';
import { DatasetRef, ImageLayer, Plane, ViewportId } from '../layout/types';
import { FrameOfReferenceUid, SeriesInstanceUid } from '~/domain/dicom/uids';
import type {
    GeneratingModel,
    InterpolatedSliceCount,
    SeriesSummary,
} from '~/utils/transforms/imageDataTransforms';

/**
 * Adapter boundary for the viewport-grid module.
 *
 * The grid talks to the host (engine, DICOM) ONLY through these interfaces.
 * The engine-backed implementation is the one the viewer uses; test doubles
 * stand in for it in behavioural tests, with no change to the grid module.
 */

export interface PixelSpacingMm {
    readonly row: number;
    readonly column: number;
}

/** Metadata for one bindable series: how it is named, and its keys. */
export interface DatasetMetadata extends SeriesSummary {
    readonly seriesInstanceUid: SeriesInstanceUid;
    readonly frameOfReferenceUid: FrameOfReferenceUid;
}

/**
 * A dataset as a picker offers it: its series, the label the user reads, and
 * the modality and series number named beside the label.
 */
export type PickableImage = Pick<
    DatasetMetadata,
    'seriesInstanceUid' | 'label' | 'modality' | 'seriesNumber'
>;

/** Opaque handle to a renderable volume; engine detail stays hidden. */
export interface RenderableVolume {
    readonly metadata: DatasetMetadata;
}

/** Resolves a series UID to a renderable volume + metadata. */
export interface DatasetAdapter {
    /**
     * The bindable datasets. The same array comes back until the datasets
     * change, so a caller can hold it as a snapshot.
     */
    listAvailable(): ReadonlyArray<DatasetMetadata>;
    resolve(seriesInstanceUid: SeriesInstanceUid): RenderableVolume | undefined;
    /**
     * Calls `listener` when the bindable datasets may have changed: one
     * joined, one left, or one was re-imported. Returns the unsubscribe.
     */
    subscribe(listener: () => void): () => void;
}

/** A sampled stored value that is read as it is: Hounsfield on CT, bare elsewhere. */
interface IntensityCursorValue {
    readonly value: number;
    readonly kind: 'hounsfield' | 'signal-intensity';
}

/**
 * A sampled PET value, with the series' quantification so it is read in
 * the unit the series carries and never as a bare number.
 */
interface PetCursorValue {
    readonly value: number;
    readonly kind: 'pet';
    /** The series' SUV record, or null when the series' units are unknown. */
    readonly suvQuantification: SuvQuantification | null;
}

/** One image's value at a point, with what its unit is read from. */
export type CursorValue = IntensityCursorValue | PetCursorValue;

/**
 * A dose sampled at a point, as the dose stores it, with no display scale
 * applied. The unit travels with the value, so a relative dose, a ratio of
 * its own plan, is never read as Gy.
 */
export interface DoseCursorValue {
    readonly value: number;
    readonly unit: DoseUnit;
    /** The sampled dose: the key of the display scale it is shown at. */
    readonly doseId: DoseId;
}

/**
 * The relative density (water = 1, unitless) a dose calculation uses at a
 * point: the override of the structure that contains it, or else the
 * density Image+ derives from the HU of its synthetic CT. One or the other,
 * never both: an override in force is what the planning system uses.
 */
export type DensityCursorValue =
    | ({ readonly kind: 'override' } & Readonly<DensityOverrideAtPoint>)
    | ({ readonly kind: 'sct' } & Readonly<SctDensity>);

/** What a tile reads under the pointer where the image it shows has a sample. */
export interface ImageCursorReadout {
    /** The value of the image the tile shows: the primary on a blended tile. */
    readonly value: CursorValue;
    /**
     * Present only on a tile that blends a fused secondary over the
     * primary: the secondary's value at the same point, or null where the
     * secondary has no sample.
     */
    readonly fusedValue?: CursorValue | null;
    /**
     * Present only where the tile draws a dose at the point: the tile shows
     * the image the dose is planned on, the dose is visible and the point
     * lies in the dose grid.
     */
    readonly dose?: DoseCursorValue;
    /**
     * Present only where a density is in force at the point: an override
     * only on a tile that shows the image of the structure set, a derived
     * density only on a tile that shows an Image+ synthetic CT, at an HU
     * inside the Image+ table.
     */
    readonly density?: DensityCursorValue;
    /** The point read, in mm in the frame of the image the tile shows. */
    readonly patientMm: PointMm;
}

/**
 * What a tile reads under the pointer off the image it shows, where it
 * draws a dose: the dose alone, with no image value.
 */
interface DoseOnlyCursorReadout {
    readonly value?: undefined;
    readonly dose: DoseCursorValue;
}

/** What a tile reads under the pointer. */
export type CursorReadout = ImageCursorReadout | DoseOnlyCursorReadout;

/**
 * Change notifications for host-owned values: subscribe + a monotonic
 * version counter usable as a `useSyncExternalStore` snapshot. Consumers
 * re-read the value getters on each notification.
 */
export interface ChangeSource {
    /** Subscribe to changes; returns an unsubscribe function. */
    subscribe(listener: () => void): () => void;
    /** Monotonic change counter — a `useSyncExternalStore` snapshot. */
    getVersion(): number;
}

/**
 * Read side of one series' window/level: change notifications plus the
 * current value. The host (the engine bus, or a test double) owns the truth; the
 * footer subscribes and re-reads {@link getWindowLevel} on each notification.
 */
export interface WindowLevelSource extends ChangeSource {
    /** The series' current window/level, or undefined before it loads. */
    getWindowLevel(): WindowLevel | undefined;
    /**
     * The current window/level as a readout shows it, in the unit the
     * series' values carry. Undefined before the series loads.
     */
    getReadout(): WindowLevelReadout | undefined;
    /**
     * The windows this series offers under a name, in the order the user
     * reads them. The acquisition window leads, so selecting it restores the
     * series to how it arrived. A preset the series cannot supply is listed
     * disabled, so the user sees that the device knows of it.
     */
    getPresets(): ReadonlyArray<WindowingPreset>;
    /**
     * The window Reset returns the series to, or undefined before the series
     * loads. The window the user set plays no part in the answer.
     */
    getResetWindowLevel(): WindowLevel | undefined;
    /**
     * The level bounds a typed value is checked against, or undefined before
     * the series loads.
     */
    getLevelRange(): WindowLevelBounds | undefined;
    /**
     * The distribution of the series' stored intensities, or null while the
     * series is unknown or its volume is not yet whole: a histogram over a
     * partly arrived volume would redraw itself as slices land.
     */
    getIntensityHistogram(): IntensityHistogram | null;
    /**
     * The acquisition record a PET series' SUV is computed from (dose,
     * half-life, injection and scan times, weight), so the unit its window
     * is read in can be explained. Null for any other series and before the
     * series loads.
     */
    getPetMetadata(): PetSeriesMetadata | null;
}

/**
 * Per-series window/level truth. Reads and writes go through the host (the
 * domain event bus, or an in-memory store in a test double) so every surface
 * showing a series agrees on its window — the grid never keeps a display
 * copy.
 */
export interface WindowingAdapter {
    /** The read source for one series (a stable instance per UID). */
    sourceFor(seriesInstanceUid: SeriesInstanceUid): WindowLevelSource;
    setWindowLevel(
        seriesInstanceUid: SeriesInstanceUid,
        windowLevel: WindowLevel
    ): void;
    /**
     * Moves the series' window by a step, from wherever the series is now.
     * A step reaches each series it is sent to as a step, so two series
     * nudged together keep their own windows rather than sharing one.
     */
    nudgeWindowLevel(
        seriesInstanceUid: SeriesInstanceUid,
        delta: WindowLevelDelta
    ): void;
}

/** A step applied to a window/level, in the series' own intensity units. */
export interface WindowLevelDelta {
    readonly windowWidth: number;
    readonly windowCenter: number;
}

/** One image offerable as a row's secondary, with the reason it is eligible. */
export interface BlendCandidate extends SeriesSummary {
    readonly seriesInstanceUid: SeriesInstanceUid;
    /**
     * How the image lies on the row's own: on the same frame of reference,
     * or through a registration, with that registration's approval status.
     */
    readonly binding: ImageBinding;
    /**
     * Names the registration that places this image on the row's own, when
     * one does. Two registrations between the same pair of images offer the
     * same image twice, and only this tells the two offers apart.
     */
    readonly registrationLabel?: string;
}

/** Read side of one row's blend state. */
export interface BlendSource extends ChangeSource {
    getCandidates(): ReadonlyArray<BlendCandidate>;
    /** The row's current secondary, or undefined when the row is not fused. */
    getCurrent(): BlendCandidate | undefined;
    /** How the row draws its pair; the defaults until moved. */
    getBlend(): BlendSettings;
}

/** Per-row secondary-image blending truth. */
export interface BlendAdapter {
    sourceFor(primarySeriesUid: SeriesInstanceUid): BlendSource;
    fuse(primarySeriesUid: SeriesInstanceUid, candidate: BlendCandidate): void;
    /** Drops the row's secondary and resets its blend to the defaults. */
    clear(primarySeriesUid: SeriesInstanceUid): void;
    /**
     * Moves the fields of the row's blend named in `patch`, leaving the
     * rest. A no-op on an unregistered row. Throws on a value no control
     * can produce; nothing is clamped.
     */
    setBlend(
        primarySeriesUid: SeriesInstanceUid,
        patch: Partial<BlendSettings>
    ): void;
}

/**
 * Where a tile's plane sits in the image it shows. `slice` when the plane
 * lies in one of that image's slice planes: the 0-based slice it cuts, or
 * undefined when the plane lies outside the image, how many slices the
 * image holds along that axis, and whether the slice cut was interpolated
 * rather than acquired. `oblique` when the plane crosses the image's
 * slices, so no slice number applies: how far it lies off them, and why.
 * Both carry how the image is sampled ({@link ImageSampling}).
 */
export type SliceInfo =
    | {
          readonly kind: 'slice';
          readonly index: number | undefined;
          readonly count: number;
          /**
           * Whether the plane lies in an interpolated slice of the image.
           * Only a plane along the acquired slices can: one across them,
           * as a sagittal plane of a transversal acquisition, is never.
           */
          readonly interpolated: boolean;
          /**
           * The patient direction the slice index rises toward, by the
           * patient axis the image's slice axis lies nearest. It follows
           * the image's own grid, so it differs between planes, and a
           * feet-first or flipped acquisition counts the other way.
           */
          readonly risesToward: DirectionLabel;
          readonly pixelSpacingMm: PixelSpacingMm;
          readonly sampling: ImageSampling;
      }
    | {
          readonly kind: 'oblique';
          /**
           * The angle in degrees between the plane's normal and the nearest
           * axis of the image's voxel grid on the pane: how far the plane
           * lies off the nearest plane the image was sampled on.
           */
          readonly angleDeg: number;
          readonly cause: ObliqueCause;
          readonly pixelSpacingMm: PixelSpacingMm;
          readonly sampling: ImageSampling;
      };

/**
 * How the image a tile shows is sampled across its acquired slices, in mm:
 * the same on every plane of the image, whatever the view reformats to.
 */
export interface ImageSampling {
    /** The distance between the centres of neighbouring slices. */
    readonly sliceSpacingMm: number;
    /**
     * The slice thickness the image's DICOM states; undefined when it
     * states none. It can differ from the spacing, as in an overlapping
     * reconstruction.
     */
    readonly sliceThicknessMm: number | undefined;
}

/**
 * Why a plane crosses the slices of the image it shows. Each reason is
 * told apart, because more than one can hold at once: an image acquired
 * tilted shown in a turned row is off its slices for both.
 */
export interface ObliqueCause {
    /**
     * The image's own tilt off the patient axes in degrees, when it was
     * acquired tilted; undefined when it lies along them.
     */
    readonly acquisitionTiltDeg: number | undefined;
    /** Whether the registration that places the image on the pane tilts it. */
    readonly registrationTilts: boolean;
    /** Whether the row's frame is turned off the patient axes. */
    readonly viewTurned: boolean;
}

/** A patient axis, as a position readout names it. */
export type PatientAxisLetter = 'X' | 'Y' | 'Z';

/**
 * Where a tile's plane lies: its signed offset in mm along its normal, with
 * the sign of the patient axis the normal lies nearest to, and that axis.
 * Both are read in the frame of the image the tile shows.
 */
export interface PlanePosition {
    readonly axis: PatientAxisLetter;
    readonly offsetMm: number;
}

/**
 * The four tile-edge direction letters in [left, top, right, bottom] order, as
 * {@link computeDirectionLabels} returns them. Derived engine-side from the
 * mounted view's real orientation so patient laterality (R/L) follows a
 * flipped or oblique view and can never mislabel.
 */
export type EdgeDirectionLabels = readonly [
    DirectionLabel,
    DirectionLabel,
    DirectionLabel,
    DirectionLabel,
];

/**
 * Change notifications for the renderer-owned shared cursor. The engine owns
 * cursor truth (tools write it directly); tiles subscribe here and re-read
 * {@link RenderAdapter.getSliceInfo}/{@link RenderAdapter.getPlanePosition} on each
 * notification.
 */
export type CursorSource = ChangeSource;

export interface MountParams {
    readonly canvas: HTMLCanvasElement;
    /**
     * The 2D tool-feedback overlay, layered exactly over {@link canvas}. Engine
     * draw tools paint cursor feedback (line rubber-band, deform / line-deform
     * cursors, box-zoom rect, auto-suggest preview) onto this second canvas, not
     * the WebGL one — so it must be a real DOM element over the image or that
     * feedback renders invisibly.
     */
    readonly overlayCanvas: HTMLCanvasElement;
    readonly volume: RenderableVolume;
    readonly plane: Plane;
}

/** What a host's per-tile overlay factory receives (the engine overlays of a tile). */
export interface TileOverlayContext {
    readonly renderer: RenderAdapter;
    /**
     * The images this tile leads, as series instance uids: each image it
     * draws that no tile before it in its row draws, and every image it
     * draws when it is shown alone. A control that acts on one image
     * mounts on the tile that leads that image.
     */
    readonly ledImages: ReadonlyArray<SeriesInstanceUid>;
}

/** One image a tile draws. */
export interface DrawnImage {
    readonly seriesInstanceUid: SeriesInstanceUid;
    /**
     * Tells the image from the other one of a blended pair, without PHI;
     * absent when the tile draws it alone.
     */
    readonly pairLabel: string | undefined;
    /**
     * The image's name, as the tile title shows it. PHI: shown masked with
     * the privacy toggle, never logged.
     */
    readonly label?: string;
    /** The AI model that generated the image; absent for an acquired image. */
    readonly generatedBy?: GeneratingModel;
    /** How many of its slices were interpolated; absent when none was. */
    readonly interpolatedSlices?: InterpolatedSliceCount;
    /**
     * Drawn blended over the row's image, whose slices the tile's slice
     * readout follows, so the readout does not mark this image's
     * interpolated slices. Absent for an image the readout follows.
     */
    readonly readoutFollowsOtherImage?: boolean;
    /** How far it was acquired tilted, in degrees; absent when it was not. */
    readonly acquisitionTiltDeg?: number;
}

/**
 * What a host is told to hang the details of an image on the name a tile
 * title gives it: the image's series, and the name as the title draws it.
 * The host returns the name, wrapped in whatever shows the details.
 */
export interface ImageDetailsContext {
    readonly seriesInstanceUid: SeriesInstanceUid;
    /** The image the named one is fused over, when it is the fused image. */
    readonly fusedOver?: SeriesInstanceUid;
    readonly name: ReactElement;
    /** The title bar the name sits in: the details hang from its lower edge. */
    readonly titleBar: HTMLElement | null;
}

/**
 * What a host is told about a row whose footer carries the controls of what
 * the host draws over the row's image.
 */
export interface RowOverlayContext {
    /** The image the row draws, which the overlays lie over. */
    readonly seriesInstanceUid: SeriesInstanceUid;
}

/** The images an owner asks the grid to show in a row each. */
export interface ImageRowsRequest {
    readonly seriesInstanceUids: ReadonlyArray<SeriesInstanceUid>;
}

/** What a host is told about a tile whose title bar carries its status. */
export interface TileStatusContext {
    /**
     * The images the tile draws, the row's own first, then the fused
     * secondary while the tile blends it.
     */
    readonly images: ReadonlyArray<DrawnImage>;
    /**
     * Whether this tile stands for its row: the row's first image tile, or
     * a tile shown alone.
     */
    readonly leadsRow: boolean;
}

/**
 * The primary image as the composition root owns it. The primary row never
 * binds an image itself: a choice made on it asks the root, and the row
 * follows the primary image the root then names.
 */
export interface PrimaryImageControls {
    /** Asks for the series `seriesInstanceUid` to become the primary image. */
    readonly choose: (seriesInstanceUid: SeriesInstanceUid) => void;
}

/** What a tile screenshot needs beyond the tile itself; file name only. */
export interface TileScreenshotOptions {
    /** The selected ROI, or undefined when none. */
    readonly roiId: RoiId | undefined;
    /** Wall-clock time the user requested the capture. */
    readonly capturedAt: Date;
}

/** A point on the page, in client (CSS pixel) coordinates. */
export interface ClientPoint {
    readonly clientX: number;
    readonly clientY: number;
}

/** An ROI under the pointer, as a tile's hover label shows it. */
export interface HoveredRoi {
    readonly name: string;
    readonly roiId: RoiId;
}

/**
 * Drives the renderer for ONE viewport tile. The grid supplies intent
 * (volume, plane) and reads back HUD values; it never touches the engine
 * directly. Window/level is not pushed here — it flows per series through
 * the {@link WindowingAdapter} and the renderer repaints from that truth.
 */
export interface RenderAdapter {
    mount(params: MountParams): void;

    /**
     * Which of the row's fused pair this tile draws. Not a mount parameter:
     * switching a tile between the blend and one side of it must not remount
     * the view, which would re-mint its Environment.
     */
    setImageLayer(layer: ImageLayer): void;
    /**
     * What the tile reads under a client point: the value of the image it
     * shows with the point read, or the dose alone off that image. Undefined
     * where it reads neither, and before mount.
     */
    readCursor(clientX: number, clientY: number): CursorReadout | undefined;
    /**
     * The visible ROI nearest a client point (the contour or point under the
     * cursor), by name and number, or undefined when none is within pick
     * range or before mount. A passive read of this tile's own structure set,
     * for the hover label and for click-to-select — never an edit, and it
     * leaves the click suppression of {@link consumeClickSuppression} alone.
     */
    readRoiAt(clientX: number, clientY: number): HoveredRoi | undefined;
    /**
     * Whether the click being handled ended a pan drag that moved, which
     * must not select. The suppression is one-shot: this clears it, so call
     * it once per click, also on a click that selects nothing, or it would
     * swallow a later click. False before mount.
     */
    consumeClickSuppression(): boolean;
    /** Undefined before mount and while the tile shows no image. */
    getSliceInfo(): SliceInfo | undefined;
    /**
     * Radiological edge letters [left, top, right, bottom] from the mounted
     * view's true orientation, or undefined before mount. The tile view-model
     * maps them onto the edges; laterality must follow the real orientation.
     */
    getEdgeLabels(): EdgeDirectionLabels | undefined;
    /**
     * Where the displayed plane lies, in the frame of the image the tile
     * shows; undefined before mount and while the tile shows no image.
     */
    getPlanePosition(): PlanePosition | undefined;
    /** Change notifications for the values behind the getters above. */
    getCursorSource(): CursorSource;
    /**
     * Patient-space mm per CSS pixel at the current display scale, for the scale
     * bar. Undefined until the first frame is laid out. Reflects the real view
     * zoom — never a nominal value.
     */
    getMmPerCssPixel(): number | undefined;
    /**
     * Change notifications for {@link getMmPerCssPixel} and the canvas size:
     * a zoom, a fit (a focus, a reset) or a resize of this tile's view. A
     * notification comes once the engine has refitted the view, so a read in
     * the listener sees the new scale.
     */
    getDisplayScaleSource(): ChangeSource;
    /**
     * The frame this tile's canvas shows right now, or undefined before
     * mount. A burned-in PHI gate reads it before a capture and binds the
     * user's confirmation to its id, which is unique across every frame
     * this renderer ever reports, re-mounts included.
     */
    getPublishedFrame(): PublishedFrame | undefined;
    /**
     * Saves the frame on the mounted view as a PNG download, named after the
     * tile's plane. Refuses an image that declares burned-in identifiers and
     * a frame that changed while it was being serialised; the caller runs
     * the confirmation policy for an unknown status first. Rejects before
     * mount and when no image can be produced.
     */
    captureScreenshot(options: TileScreenshotOptions): Promise<void>;
    dispose(): void;
}

/**
 * Read side of one row's oblique frame: the rotation, in patient space,
 * that every tile of the row shows on top of its own anatomical plane. The
 * host owns the truth (the row's Environment on the engine route); the
 * tiles subscribe and re-read {@link getFrameRotation} on each
 * notification.
 */
export interface FrameRotationSource extends ChangeSource {
    /** The row's frame rotation; identity while the row is patient-aligned. */
    getFrameRotation(): ReadonlyMat3;
}

/**
 * Hides how the host backs tiles with Environments: the grid asks only for
 * per-tile renderers, and reads and resets what a row's tiles share, their
 * oblique frame, through the row's series.
 */
export interface EnvironmentAdapter {
    /**
     * The tile id becomes the mounted view's stable identity in the engine,
     * so tool gates can tell same-plane tiles apart. Unique per grid cell.
     */
    createTileRenderer(tileId: ViewportId): RenderAdapter;
    releaseTileRenderer(renderer: RenderAdapter): void;
    /** The read source for one row's frame rotation (a stable instance per UID). */
    frameRotationSourceFor(
        seriesInstanceUid: SeriesInstanceUid
    ): FrameRotationSource;
    /** Returns the row's tiles to their patient-aligned planes. */
    resetFrameRotation(seriesInstanceUid: SeriesInstanceUid): void;
}

/** The layout's reference to the dataset `metadata` describes: its key. */
export function toDatasetRef(metadata: DatasetMetadata): DatasetRef {
    return { seriesInstanceUid: metadata.seriesInstanceUid };
}
